import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { monthlyPriceFor, setupFeeFor } from '../../config/pricing';

function MarketingPricing() {
  const [openFaq, setOpenFaq] = useState(null);

  const tiers = [
    {
      id: 'free_trial',
      name: 'Free Trial',
      setupFee: setupFeeFor('free_trial'),
      monthlyFee: monthlyPriceFor('free_trial'),
      description: 'Try DeedStack™ risk-free for 14 days',
      limits: {
        activeLoans: 3,
        properties: 10,
        adminUsers: 1
      },
      features: [
        'Customer portal',
        'Basic email notifications',
        'HOA tracking',
        'Sold properties showcase'
      ],
      notIncluded: [
        'Payment processing (sandbox only)',
        'eBay integration',
        'Tax escrow',
        'Custom domain'
      ],
      buttonText: 'Start Free Trial',
      buttonLink: '/demo'
    },
    {
      id: 'starter',
      name: 'Starter',
      setupFee: setupFeeFor('starter'),
      monthlyFee: monthlyPriceFor('starter'),
      description: 'Perfect for getting started',
      limits: {
        activeLoans: 25,
        properties: 50,
        adminUsers: 1
      },
      features: [
        'Everything in Free Trial PLUS:',
        'Live payment processing (Square, Stripe, or custom)',
        'Logo upload',
        'Custom domain',
        'Backup & export',
        '"Powered by DeedStack™" badge'
      ],
      buttonText: 'Get Started',
      buttonLink: '/demo'
    },
    {
      id: 'pro',
      name: 'Pro',
      setupFee: setupFeeFor('pro'),
      monthlyFee: monthlyPriceFor('pro'),
      popular: true,
      description: 'Most popular for growing businesses',
      limits: {
        activeLoans: 100,
        properties: 200,
        adminUsers: 3
      },
      features: [
        'Everything in Starter PLUS:',
        'eBay integration',
        'Tax escrow tracking',
        'Auction calendar',
        'Multi-user (3 seats)',
        'SMS notifications',
        'Small badge only'
      ],
      buttonText: 'Get Started',
      buttonLink: '/demo'
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      setupFee: setupFeeFor('enterprise'),
      monthlyFee: monthlyPriceFor('enterprise'),
      description: 'For high-volume operations',
      limits: {
        activeLoans: '∞',
        properties: '∞',
        adminUsers: '∞'
      },
      features: [
        'Everything in Pro PLUS:',
        'Bulk import',
        'API access',
        'Custom email templates',
        'No DeedStack™ branding',
        'Priority phone support'
      ],
      buttonText: 'Contact Sales',
      buttonLink: '/contact'
    }
  ];

  const faqs = [
    {
      question: 'What happens after my free trial ends?',
      answer: 'Your account will be downgraded to read-only mode. You can upgrade to a paid plan at any time to restore full access. Your data is never deleted.'
    },
    {
      question: 'Can I change plans later?',
      answer: 'Yes! You can upgrade or downgrade your plan at any time. When upgrading, you pay the difference. When downgrading, credits are applied to future months.'
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards (Visa, MasterCard, American Express) and ACH bank transfers for annual plans.'
    },
    {
      question: 'Is there a contract or commitment?',
      answer: 'No long-term contracts. All plans are month-to-month after the initial setup fee. Cancel anytime with no penalties.'
    },
    {
      question: 'What is the setup fee for?',
      answer: 'The one-time setup fee covers account configuration, initial training, data import assistance, and custom domain setup. It ensures you get started on the right foot.'
    },
    {
      question: 'Do you offer discounts for annual billing?',
      answer: 'Yes! Contact us for annual billing options and receive 2 months free on any paid plan.'
    }
  ];

  const comparisonFeatures = [
    { name: 'Active Loans', free: '3', starter: '25', pro: '100', enterprise: '∞' },
    { name: 'Properties Listed', free: '10', starter: '50', pro: '200', enterprise: '∞' },
    { name: 'Admin Users', free: '1', starter: '1', pro: '3', enterprise: '∞' },
    { name: 'Customer Portal', free: true, starter: true, pro: true, enterprise: true },
    { name: 'Payment Processing (Square, Stripe, or custom)', free: 'Sandbox', starter: true, pro: true, enterprise: true },
    { name: 'Custom Domain', free: false, starter: true, pro: true, enterprise: true },
    { name: 'eBay Integration', free: false, starter: false, pro: true, enterprise: true },
    { name: 'Tax Escrow', free: false, starter: false, pro: true, enterprise: true },
    { name: 'Auction Calendar', free: false, starter: false, pro: true, enterprise: true },
    { name: 'Bulk Import', free: false, starter: false, pro: false, enterprise: true },
    { name: 'API Access', free: false, starter: false, pro: false, enterprise: true },
    { name: 'White-Label', free: 'Watermark', starter: 'Powered by', pro: 'Small badge', enterprise: 'None' }
  ];

  return (
    <div className="marketing-pricing">
      {/* Hero */}
      <section className="marketing-hero marketing-hero-short">
        <div className="marketing-hero-content">
          <h1 className="marketing-hero-title">Simple, Transparent Pricing</h1>
          <p className="marketing-hero-subtitle">
            Choose the plan that fits your business. All plans include a 14-day free trial.
          </p>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="marketing-pricing-tiers">
        <div className="container">
          <div className="pricing-tiers-grid">
            {tiers.map((tier) => (
              <div
                key={tier.id}
                className={`pricing-tier-card ${tier.popular ? 'pricing-tier-popular' : ''}`}
              >
                {tier.popular && (
                  <div className="pricing-popular-badge">Most Popular</div>
                )}

                <h3 className="pricing-tier-name">{tier.name}</h3>
                <p className="pricing-tier-description">{tier.description}</p>

                <div className="pricing-tier-price">
                  {tier.setupFee === 0 && tier.monthlyFee > 0 ? (
                    <>
                      <span className="pricing-amount">${tier.monthlyFee}</span>
                      <span className="pricing-period">/month</span>
                    </>
                  ) : tier.setupFee === 0 ? (
                    <span className="pricing-amount">$0</span>
                  ) : (
                    <>
                      <span className="pricing-amount">${tier.setupFee.toLocaleString()}</span>
                      <span className="pricing-setup">setup</span>
                    </>
                  )}
                </div>

                {tier.setupFee === 0 && tier.monthlyFee > 0 ? (
                  <div className="pricing-no-setup">No setup fee</div>
                ) : tier.monthlyFee > 0 && (
                  <div className="pricing-monthly">+ ${tier.monthlyFee}/mo</div>
                )}

                <div className="pricing-limits">
                  <div className="pricing-limit-item">
                    <span className="pricing-limit-value">{tier.limits.activeLoans}</span>
                    <span className="pricing-limit-label">Active Loans</span>
                  </div>
                  <div className="pricing-limit-item">
                    <span className="pricing-limit-value">{tier.limits.properties}</span>
                    <span className="pricing-limit-label">Properties</span>
                  </div>
                </div>

                <ul className="pricing-features">
                  {tier.features.map((feature, index) => (
                    <li key={index} className={feature.includes('PLUS:') ? 'pricing-feature-header' : ''}>
                      {!feature.includes('PLUS:') && <span className="pricing-check">&#10003;</span>}
                      {feature}
                    </li>
                  ))}
                </ul>

                {tier.notIncluded && (
                  <ul className="pricing-not-included">
                    {tier.notIncluded.map((feature, index) => (
                      <li key={index}>
                        <span className="pricing-x">&#10007;</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                )}

                <div className="pricing-tier-action">
                  <Link
                    to={tier.buttonLink}
                    className={`btn ${tier.popular ? 'pricing-btn-popular' : 'btn-primary'}`}
                  >
                    {tier.buttonText}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Comparison */}
      <section className="marketing-comparison">
        <div className="container">
          <h2 className="section-title">Feature Comparison</h2>

          <div className="comparison-table-wrapper">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Free Trial</th>
                  <th>Starter</th>
                  <th className="popular-column">Pro</th>
                  <th>Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {comparisonFeatures.map((feature, index) => (
                  <tr key={index}>
                    <td>{feature.name}</td>
                    <td>{renderFeatureValue(feature.free)}</td>
                    <td>{renderFeatureValue(feature.starter)}</td>
                    <td className="popular-column">{renderFeatureValue(feature.pro)}</td>
                    <td>{renderFeatureValue(feature.enterprise)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="marketing-faq">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>

          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`faq-item ${openFaq === index ? 'open' : ''}`}
              >
                <button
                  className="faq-question"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  {faq.question}
                  <span className="faq-toggle">{openFaq === index ? '−' : '+'}</span>
                </button>
                {openFaq === index && (
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta marketing-cta">
        <div className="cta-content">
          <h2>Ready to Get Started?</h2>
          <p>Start your 14-day free trial today. No credit card required.</p>
          <Link to="/demo" className="btn btn-large btn-cta-primary">
            Start Free Trial
          </Link>
        </div>
      </section>
    </div>
  );
}

function renderFeatureValue(value) {
  if (value === true) {
    return <span className="feature-check">&#10003;</span>;
  }
  if (value === false) {
    return <span className="feature-x">&#10007;</span>;
  }
  return value;
}

export default MarketingPricing;
